import { apiService } from './api';
import { getApiConfig } from '../config/api';
import { handleUnauthorized } from '../utils/jwtUtils';

// Get API configuration
const apiConfig = getApiConfig();

// Types for profile management
export interface UserProfileDto {
  userId: number;
  username: string;
  firstName: string;
  lastName: string;
  email: string;
  mobileNumber: string;
  profilePictureUrl?: string;
  createdAt: string;
  updatedAt: string;
}

export interface UpdatePersonalInfoRequest {
  firstName: string;
  lastName: string;
  email: string;
  mobileNumber: string;
  password?: string;
  isEmailVerified?: boolean;
}

export interface UpdatePasswordRequest {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

/**
 * One-time password-add for social-login accounts. Posted to
 * /api/Auth/set-password; server rejects if a password already exists.
 */
export interface SetPasswordRequest {
  newPassword: string;
  confirmPassword: string;
}

export interface ProfilePictureResponseDto {
  success: boolean;
  message: string;
  profilePictureUrl: string;
}

export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
}

class ProfileApiService {
  // Get user profile information
  async getUserProfile(): Promise<ApiResponse<UserProfileDto>> {
    return apiService.request<UserProfileDto>('/Account/profile');
  }

  // Update personal information
  async updatePersonalInfo(data: UpdatePersonalInfoRequest): Promise<ApiResponse<UserProfileDto>> {
    return apiService.request<UserProfileDto>('/Account/personal-info', {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  // Upload profile picture
  async uploadProfilePicture(file: File): Promise<ApiResponse<ProfilePictureResponseDto>> {
    const formData = new FormData();
    formData.append('file', file);

    try {
      console.log('Profile API: Starting upload process...');
      console.log('Profile API: File details:', {
        name: file.name,
        size: file.size,
        type: file.type
      });

      // Prepare request
      const requestUrl = `${apiConfig.BASE_URL}/Account/profile-photo`;
      console.log('Profile API: Request URL:', requestUrl);


      // Make the request
      const response = await fetch(requestUrl, {
        method: 'POST',
        body: formData,
        credentials: 'include'
      });

      console.log('Profile API: Response status:', response.status);
      console.log('Profile API: Response headers:', Object.fromEntries(response.headers.entries()));

      if (response.ok) {
        const responseData = await response.json();
        console.log('Profile API: Success response:', responseData);
        return {
          success: true,
          data: responseData.data || responseData,
          message: responseData.message
        };
      } else if (response.status === 401) {
        // Token expired, try to refresh
        console.log('Profile API: Token expired, attempting refresh...');

        const refreshResult = await apiService.refreshAccessToken();
        if (refreshResult.success) {
          // Retry with new token
          console.log('Profile API: Retrying with new token...');

          const retryResponse = await fetch(requestUrl, {
            method: 'POST',
            body: formData,
            credentials: 'include'
          });

          if (retryResponse.ok) {
            const retryData = await retryResponse.json();
            console.log('Profile API: Retry success:', retryData);
            return {
              success: true,
              data: retryData.data || retryData,
              message: retryData.message
            };
          } else if (retryResponse.status === 401) {
            // Still 401 after refresh - account likely deleted, redirect immediately
            handleUnauthorized('Profile API (Retry)');
            const result: any = {
              success: false,
              message: 'Session expired. Please login again.',
              error: 'Session expired. Please login again.',
              data: null,
            }
            return result;
          } else {
            const errorData = await retryResponse.json().catch(() => ({ message: 'Unknown error' }));
            console.error('Profile API: Retry failed:', errorData);
            return {
              success: false,
              error: errorData.message || errorData.error || `HTTP ${retryResponse.status}: ${retryResponse.statusText}`
            };
          }
        } else {
          // Refresh failed - account deleted or token invalid, redirect immediately
          handleUnauthorized('Profile API');
          const result: any = {
            success: false,
            message: 'Session expired. Please login again.',
            error: 'Session expired. Please login again.',
            data: null,
          }
          return result;
        }
      } else {
        // Handle other error status codes
        let errorData;
        try {
          errorData = await response.json();
        } catch {
          errorData = { message: 'Failed to parse error response' };
        }

        console.error('Profile API: Upload failed with status', response.status, ':', errorData);
        return {
          success: false,
          error: errorData.message || errorData.error || `HTTP ${response.status}: ${response.statusText}`
        };
      }
    } catch (error) {
      console.error('Profile API: Upload failed with exception:', error);
      return {
        success: false,
        error: `Upload failed: ${error instanceof Error ? error.message : 'Unknown error'}`
      };
    }
  }

  // Delete profile picture
  async deleteProfilePicture(): Promise<ApiResponse<{ message: string }>> {
    return apiService.request<{ message: string }>('/Account/profile-photo', {
      method: 'DELETE'
    });
  }

  // Update password
  async updatePassword(data: UpdatePasswordRequest): Promise<ApiResponse<{ message: string }>> {
    try {
      console.log('Profile API: Attempting password update with data:', data);

      const response = await apiService.request<{ message: string }>('/Auth/change-password', {
        method: 'POST',
        body: JSON.stringify(data)
      });

      console.log('Profile API: Password update response:', response);
      return response;
    } catch (error: any) {
      console.error('Profile API: Error during password update:', error);
      return {
        success: false,
        error: error.message || 'Failed to update password'
      };
    }
  }

  // Set an initial password on a social-login account that doesn't have one yet.
  // Prerequisite for the credentialed email-change flow on social accounts.
  async setPassword(data: SetPasswordRequest): Promise<ApiResponse<{ message: string }>> {
    try {
      const response = await apiService.request<{ message: string }>('/Auth/set-password', {
        method: 'POST',
        body: JSON.stringify(data)
      });
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to set password'
      };
    }
  }
}

export const profileApiService = new ProfileApiService();
